import { SocialMediaDriver } from "../interaction_tracking/network";
import { GenerationConstraint } from "./generation.models";
import { StimuliBank } from "./stimuli.bank";

class MissingStimuliFile extends Error {}

export class MissingStageError extends Error {}

export class NoRemainingStagesError extends Error {}

export class MultiStageGenerator {
  private networkDriver: SocialMediaDriver;
  private subjectId: string;
  private bank: StimuliBank;
  private stage?: number;

  constructor(driver: SocialMediaDriver, bank: StimuliBank, subjectId: string) {
    this.networkDriver = driver;
    this.bank = bank;
    this.subjectId = subjectId;
  }

  async overwriteStimuli(): Promise<StimuliBank> {
    const multiStageStrategy =
      this.bank.generationStrategy.randomization?.multiStage;

    if (multiStageStrategy == null || !multiStageStrategy.enabled) {
      // If multi stage isn't set or is disabled, do nothing.
      return this.bank;
    }

    try {
      this.bank = await this._fetchExistingBank();
    } catch (error) {
      if (error instanceof MissingStimuliFile) {
        await this._saveNewBank();
      } else {
        throw error;
      }
    }

    const numStages = multiStageStrategy.stages;

    if (numStages <= 1) {
      return this.bank;
    }

    this.stage = this.lookupCurrentStage();

    if (this.stage == null || this.stage < 1) {
      throw new MissingStageError();
    }

    if (this.stage > numStages) {
      throw new NoRemainingStagesError();
    }

    console.debug(
      "quizzes in multi-stage before slicing",
      this.bank.quizzes,
      numStages,
      this.stage,
    );
    const quizzes = this._sliceListByStage(
      this.stage,
      numStages,
      this.bank.quizzes,
      this.bank.generationStrategy.constraints?.quiz,
    );
    console.debug("quizzes in multi-stage after slicing", quizzes);

    const authors = this._sliceListByStage(
      this.stage,
      numStages,
      this.bank.authors,
    )!;

    return {
      ...this.bank,
      authors: authors,
      avatars: this._sliceListByStage(
        this.stage,
        numStages,
        this.bank.avatars,
      )!,
      quizzes: quizzes,
      videos: this._sliceListByStage(this.stage, numStages, this.bank.videos),
      filler: this._sliceListByStage(this.stage, numStages, this.bank.filler),
      friendPosts: this._sliceListByStage(
        this.stage,
        numStages,
        this.bank.friendPosts,
      ),
      articles: this._sliceListByStage(
        this.stage,
        numStages,
        this.bank.articles,
      )!,
      advertisements: this._sliceListByStage(
        this.stage,
        numStages,
        this.bank.advertisements,
      )!,
      infographics: this._sliceListByStage(
        this.stage,
        numStages,
        this.bank.infographics,
      )!,
      neutralInfographics: this._sliceListByStage(
        this.stage,
        numStages,
        this.bank.neutralInfographics,
      )!,
      memes: this._sliceListByStage(this.stage, numStages, this.bank.memes)!,
      sidebar: {
        ...this.bank.sidebar,
        sponsored:
          this._sliceListByStage(
            this.stage,
            numStages,
            this.bank.sidebar?.sponsored,
          ) ?? [],
        authors: authors,
        avatars:
          this._sliceListByStage(
            this.stage,
            numStages,
            this.bank.sidebar?.avatars,
          ) ?? [],
        shortcuts: this.bank.sidebar?.shortcuts ?? [],
      },
    };
  }

  _sliceListByStage<T extends { id?: string }>(
    currentStage: number,
    numStages: number,
    list?: T[],
    constraints?: GenerationConstraint,
  ): T[] | undefined {
    if (list == null) {
      return undefined;
    }

    let numberPerStage = Math.round(list.length / numStages);

    if (
      constraints?.multistage_constraint &&
      constraints.multistage_constraint.enabled
    ) {
      console.debug("using multistage generation override");

      if (
        constraints.multistage_constraint.stages != null &&
        constraints.multistage_constraint.stages.find(
          (stage) => stage === currentStage,
        )
      ) {
        /// This setting acts an override to the multistage setting, and just passes through the entire stimuli list for this category, on the matching stage.
        console.debug("multistage generation override result=", list);
        return list;
      }
    }

    if (numberPerStage < 1) {
      if (currentStage == 1) {
        /// We need to make sure we at least include one item per stage.
        return [list[0]];
      } else {
        return [];
      }
    }

    const start = (currentStage - 1) * numberPerStage;

    if (currentStage === numStages) {
      /// On the last stage, slice to the very end of the list to ensure we don't miss any data points.
      return list.slice(start);
    }

    return list.slice(start, start + numberPerStage);
  }

  async _fetchExistingBank(): Promise<StimuliBank> {
    let response: StimuliBank | Response;

    try {
      response = await this.networkDriver.getStimuliBank(this.subjectId);
    } catch (error) {
      if (!(error instanceof Response)) throw error;

      if (error.status == 404) {
        throw new MissingStimuliFile();
      } else {
        throw new Error(
          `Stimuli data lookup failed with status code ${error.status}: ${error.statusText}`,
        );
      }
    }

    return response as StimuliBank;
  }

  async _saveNewBank() {
    return this.networkDriver.putStimuliBank(this.bank, this.subjectId);
  }

  lookupCurrentStage(): number | undefined {
    const params = new URLSearchParams(window.location.search);
    return params.has("c") ? Number.parseInt(params.get("c")!) : undefined;
  }
}
